<template>
  <div class="step-container client" :class="containerClass">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Please provide details about the inherited IRA. This information is
          required by your advisor and the custodian to complete the transfer.
        </p>
      </div>
    </help-drawer>

    <v-form name="PershingInheritedIRA" class="form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_DECEDENT.id" :instance="instance" />

        <!-- Heading -->
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-decedent-header">
              <span class="headerlight">
                Pershing Inherited <strong>IRA Details</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>

        <!-- Sync message -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving inherited IRA information...</p>
        </div>

        <!-- Re-open form message -->
        <data-submitted-message v-if="isPershingInheritedIRAComplete(instance) && formHidden && !isSyncing" :submitted="isPershingInheritedIRAComplete(instance)"
        v-on:hide-form="toggleFormState" />

        <!-- === FORM === -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container">
              <!-- 1. Decedent relationship -->
              <div class="faux-label required">Decedent Relationship</div>
              <v-select 
                v-model="relationship" 
                :items="relationshipOptions" 
                :error-messages="relationshipErrors" 
                :backgroundColor="inputBackgroundColor"
                outlined
                dense 
                hide-details="auto" 
                placeholder="Choose Relationship"
                @change="$v.relationship.$touch()" 
                class="mb-2"
                data-test-id="relationship-select"
              />

              <!-- 2. Inherited IRA Inheritor -->
              <div class="faux-label required">Inherited IRA Inheritor</div>
              <v-select 
                v-model="inheritor" 
                :items="inheritorOptions" 
                :error-messages="inheritorErrors" 
                :backgroundColor="inputBackgroundColor"
                outlined
                dense 
                hide-details="auto" 
                placeholder="Choose Inheritor Type"
                @change="$v.inheritor.$touch()" 
                class="mb-2"
                data-test-id="inheritor-select"
              />

              <!-- 3. Inheritance structure -->
              <div class="faux-label required">Inheritance Structure</div>
              <v-select 
                v-model="structure" 
                :items="structureOptions" 
                :error-messages="structureErrors" 
                :backgroundColor="inputBackgroundColor"
                outlined 
                dense
                hide-details="auto" 
                placeholder="Choose Structure"
                @change="$v.structure.$touch(); inheritanceType = null" 
                class="mb-2"
                data-test-id="structure-select"
              />

              <!-- 4. Inheritance type (conditional) -->
              <div v-if="structure">
                <div class="faux-label required">Inheritance Type</div>
                <v-select 
                  v-model="inheritanceType" 
                  :items="filteredInheritanceTypes"
                  :error-messages="inheritanceTypeErrors" 
                  :backgroundColor="inputBackgroundColor"
                  outlined 
                  dense 
                  hide-details="auto"
                  placeholder="Choose Type"
                  @change="$v.inheritanceType.$touch()" 
                  class="mb-2"
                  data-test-id="inheritance-type-select"
                />
              </div>

              <!-- 5. Payout election -->
              <div class="faux-label required">Payout Election</div>
              <v-select 
                v-model="payout" 
                :items="payoutOptions" 
                :error-messages="payoutErrors" 
                :backgroundColor="inputBackgroundColor"
                outlined 
                dense
                hide-details="auto" 
                placeholder="Choose Payout"
                @change="$v.payout.$touch()" 
                class="mb-2"
                data-test-id="payout-select"
              />

              <!-- 6. Decedent details -->
              <label class="required">Full Legal Name</label>
              <v-text-field 
                v-model.trim="fullName" 
                :error-messages="fullNameErrors" 
                :backgroundColor="inputBackgroundColor"
                outlined 
                dense 
                hide-details="auto"
                @blur="$v.fullName.$touch()" 
                class="mb-2"
                data-test-id="full-name-input"
              />

              <v-row>
                <v-col>
                  <label class="required">Date of Birth</label>
                  <v-text-field 
                    v-model="dob" 
                    :placeholder="INPUT_DATE_FMT"
                    maxlength="8" 
                    inputmode="numeric" 
                    :error-messages="dobErrors" 
                    :backgroundColor="inputBackgroundColor"
                    outlined 
                    dense
                    hide-details="auto" 
                    @input="$v.dob.$touch()" 
                    @blur="standardizeDob()"
                    data-test-id="dob-input"
                  >
                    <template v-slot:append>
                      <span class="cal-icon">
                        <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                      </span>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col>
                  <label class="required">Date of Death</label>
                  <v-text-field 
                    v-model="dod" 
                    :placeholder="INPUT_DATE_FMT"
                    maxlength="8" 
                    inputmode="numeric" 
                    :error-messages="dodErrors" 
                    :backgroundColor="inputBackgroundColor"
                    outlined 
                    dense
                    hide-details="auto" 
                    @input="$v.dod.$touch()" 
                    @blur="standardizeDod()"
                    data-test-id="dod-input"
                  >
                    <template v-slot:append>
                      <span class="cal-icon">
                        <v-icon small color="#9b9b9b">fas fa-calendar-alt</v-icon>
                      </span>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- SSN with proper formatting -->
              <label class="required">Social Security Number</label>
              <div class="ssn-container">
                <v-text-field
                  class="ssn1"
                  outlined
                  maxlength="3"
                  v-model.trim="ssn1"
                  :backgroundColor="inputBackgroundColor"
                  @input="touchSSN(); focusSSN2();"
                  @blur="touchSSN()"
                  hide-details="auto"
                  inputmode="numeric"
                  dense
                  data-test-id="ssn1-input"
                />
                <div class="dash">
                  <i class="fas fa-minus"></i>
                </div>
                <v-text-field
                  outlined
                  class="ssn2"
                  maxlength="2"
                  ref="ssn2"
                  v-model.trim="ssn2"
                  :backgroundColor="inputBackgroundColor"
                  @input="touchSSN(); focusSSN3();"
                  @blur="touchSSN()"
                  hide-details="auto"
                  inputmode="numeric"
                  dense
                  data-test-id="ssn2-input"
                />
                <div class="dash">
                  <i class="fas fa-minus"></i>
                </div>
                <v-text-field
                  class="ssn3"
                  outlined
                  maxlength="4"
                  ref="ssn3"
                  v-model.trim="ssn3"
                  :backgroundColor="inputBackgroundColor"
                  @blur="touchSSN()"
                  @input="touchSSN()"
                  hide-details="auto"
                  inputmode="numeric"
                  dense
                  data-test-id="ssn3-input"
                />
              </div>
              <div class="error-area" v-if="hasSSNErrors">{{ ssnErrors }}</div>

              <v-row>
                <v-col>
                  <label class="required">Country of Citizenship</label>
                  <v-select 
                    v-model="citizenship" 
                    :items="countryOptions" 
                    :error-messages="citizenshipErrors" 
                    :backgroundColor="inputBackgroundColor"
                    outlined 
                    dense
                    hide-details="auto" 
                    placeholder="Choose Country"
                    @change="$v.citizenship.$touch()" 
                    class="mb-2"
                    data-test-id="citizenship-select"
                  />
                </v-col>
                <v-col>
                  <label class="required">Source of Wealth</label>
                  <v-select 
                    v-model="wealthSource" 
                    :items="wealthSourceOptions" 
                    :error-messages="wealthErrors" 
                    :backgroundColor="inputBackgroundColor"
                    outlined 
                    dense
                    hide-details="auto" 
                    placeholder="Choose Source"
                    @change="$v.wealthSource.$touch()" 
                    class="mb-2"
                    data-test-id="wealth-source-select"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-form>

    <bottom-nav @go-prev="prev()" @go-next="next()" />
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import {
  MENUS,
  WEALTH_SOURCE_LIST,
  INPUT_DATE_FMT,
  US_DATE_SHORT,
  ISO_DATE_SHORT,
  validInputDate,
  DECEDENT_RELATIONSHIP_TYPES,
  DECEDENT_INHERITANCE_STRUCTURE_TYPES,
  DECEDENT_INDIVIDUAL_INHERITANCE_TYPES,
  DECEDENT_ENTITY_INHERITANCE_TYPES,
  DECEDENT_PAYOUT_TYPES,
  COUNTRIES,
} from '@/common/constants';
import { required } from 'vuelidate/lib/validators';

/* SSN validator reused from SSN.vue */
const validateSSN = (value) => {
  const v = value ? value.match(/\d/g) : null;
  return v && v.length === 9;
};

/* Date-of-death must be after DOB */
const dateAfterDob = (value, vm) => {
  if (!validInputDate(value) || !validInputDate(vm.dob)) return true;
  
  // Handle both input format (MMDDYYYY) and formatted display format (MM/DD/YYYY)
  const dob = window.moment(vm.dob, [INPUT_DATE_FMT, US_DATE_SHORT], true);
  const dod = window.moment(value, [INPUT_DATE_FMT, US_DATE_SHORT], true);
  
  // Check if both dates are valid before comparing
  if (!dob.isValid() || !dod.isValid()) return true;
  
  return dod.isAfter(dob);
};

export default {
  name: 'PershingInheritedIRA',
  props: {
    name: String,
    label: String,
  },
  components: {
    HelpDrawer,
    BottomNav,
    DataSubmittedMessage,
    SyncingIndicator,
  },
  mixins: [InterviewClientMixin],

  data() {
    return {
      MENUS,
      INPUT_DATE_FMT,

      /* form fields */
      relationship: null,
      inheritor: null,
      structure: null,
      inheritanceType: null,
      payout: null,
      fullName: null,
      dob: null,
      dod: null,
      ssn1: null,
      ssn2: null,
      ssn3: null,
      citizenship: 'United States',
      wealthSource: null,
      
      /* Options */
      inheritorOptions: [
        { text: 'Original', value: 'Original' },
        { text: 'Successor', value: 'Successor' }
      ],
    };
  },

  validations() {
    return {
      relationship: { required },
      inheritor: { required },
      structure: { required },
      inheritanceType: {
        required,
        /* only valid if option matches selected structure */
        valid(value) {
          if (!value) return true;
          return (
            this.inheritanceTypeMap[this.structure] || []
          ).includes(value);
        },
      },
      payout: { required },
      fullName: { required },
      dob: { required, validInputDate },
      dod: { required, validInputDate, dateAfterDob },
      ssn: { validateSSN },
      citizenship: { required },
      wealthSource: { required },
      validationGroup: [
        'relationship',
        'inheritor',
        'structure',
        'inheritanceType',
        'payout',
        'fullName',
        'dob',
        'dod',
        'ssn',
        'citizenship',
        'wealthSource',
      ],
    };
  },

  computed: {
    /* dynamic error helpers */
    relationshipErrors() {
      const e = [];
      if (!this.$v.relationship.$dirty) return e;
      if (!this.$v.relationship.required) e.push('Selection required');
      return e;
    },
    inheritorErrors() {
      const e = [];
      if (!this.$v.inheritor.$dirty) return e;
      if (!this.$v.inheritor.required) e.push('Selection required');
      return e;
    },
    structureErrors() {
      const e = [];
      if (!this.$v.structure.$dirty) return e;
      if (!this.$v.structure.required) e.push('Selection required');
      return e;
    },
    inheritanceTypeErrors() {
      const e = [];
      if (!this.$v.inheritanceType.$dirty) return e;
      if (!this.$v.inheritanceType.required) e.push('Selection required');
      return e;
    },
    payoutErrors() {
      const e = [];
      if (!this.$v.payout.$dirty) return e;
      if (!this.$v.payout.required) e.push('Selection required');
      return e;
    },
    fullNameErrors() {
      const e = [];
      if (!this.$v.fullName.$dirty) return e;
      if (!this.$v.fullName.required) e.push('Full name required');
      return e;
    },
    dobErrors() {
      const e = [];
      if (!this.$v.dob.$dirty) return e;
      if (!this.$v.dob.required) e.push('Date of birth required');
      if (!this.$v.dob.validInputDate) e.push('Must be a valid date (MMDDYYYY)');
      return e;
    },
    dodErrors() {
      const e = [];
      if (!this.$v.dod.$dirty) return e;
      if (!this.$v.dod.required) e.push('Date of death required');
      if (!this.$v.dod.validInputDate) e.push('Must be a valid date (MMDDYYYY)');
      if (!this.$v.dod.dateAfterDob) e.push('Must be after date of birth');
      return e;
    },
    citizenshipErrors() {
      const e = [];
      if (!this.$v.citizenship.$dirty) return e;
      if (!this.$v.citizenship.required) e.push('Selection required');
      return e;
    },
    wealthErrors() {
      const e = [];
      if (!this.$v.wealthSource.$dirty) return e;
      if (!this.$v.wealthSource.required) e.push('Selection required');
      return e;
    },

    /* Form visibility and sync status */
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_DECEDENT.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    containerClass() {
      return this.formHidden ? 'form-hidden' : '';
    },
    helpTitle() {
      return 'What does this mean?';
    },
    instance() {
      return { name: this.name, label: this.label };
    },

    /* Cascading dropdowns */
    inheritanceTypeMap() {
      return {
        [DECEDENT_INHERITANCE_STRUCTURE_TYPES.INDIVIDUAL]: this.individualTypes,
        [DECEDENT_INHERITANCE_STRUCTURE_TYPES.ENTITY]: this.entityTypes,
      };
    },
    filteredInheritanceTypes() {
      return this.inheritanceTypeMap[this.structure] || [];
    },

    /* SSN validation */
    ssn() {
      return `${this.ssn1 || ''}${this.ssn2 || ''}${this.ssn3 || ''}`;
    },
    ssnErrors() {
      return 'SSN must be exactly 9 digits';
    },
    hasSSNErrors() {
      return this.$v.ssn.$dirty && !this.$v.ssn.validateSSN;
    },
  },

  methods: {
    /* Date standardization */
    standardizeDob() {
      this.$v.dob.$touch();
      if (validInputDate(this.dob)) {
        this.dob = this.dob
          ? window
              .moment(this.dob, INPUT_DATE_FMT, true)
              .format(US_DATE_SHORT)
          : null;
      }
    },
    standardizeDod() {
      this.$v.dod.$touch();
      if (validInputDate(this.dod)) {
        this.dod = this.dod
          ? window
              .moment(this.dod, INPUT_DATE_FMT, true)
              .format(US_DATE_SHORT)
          : null;
      }
    },

    /* Navigation */
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      /* payload sent to backend mapper */
      const decedentData = {
        fullLegalName: this.fullName,
        inheritor: this.inheritor,
        dob: this.dob
          ? window
            .moment(this.dob, US_DATE_SHORT, true)
            .format(ISO_DATE_SHORT)
          : null,
        dateOfDeath: this.dod
          ? window
            .moment(this.dod, US_DATE_SHORT, true)
            .format(ISO_DATE_SHORT)
          : null,
        ssn: this.ssn,
        countryOfCitizenship: this.citizenship,
        sourceOfWealth: this.wealthSource,
        payoutElection: this.payout,
      };

      // Map relationship to enum value
      if (this.relationship === DECEDENT_RELATIONSHIP_TYPES.SPOUSE) {
        decedentData.decedentRelationship = 'Spouse Decedent';
      } else if (this.relationship === DECEDENT_RELATIONSHIP_TYPES.NON_SPOUSE) {
        decedentData.decedentRelationship = 'Non-Spouse Decedent';
      }

      // Map inheritance type based on structure
      if (this.structure === DECEDENT_INHERITANCE_STRUCTURE_TYPES.INDIVIDUAL) {
        decedentData.inheritanceType = this.inheritanceType;
        decedentData.entityInheritanceType = null;
      } else if (this.structure === DECEDENT_INHERITANCE_STRUCTURE_TYPES.ENTITY) {
        decedentData.inheritanceType = null;
        decedentData.entityInheritanceType = this.inheritanceType;
      }

      const data = {
        instance: this.instance,
        decedent: decedentData,
      };

      const pageName = MENUS.CLIENT.PERSHING_DECEDENT.id;

      /* Mark page as syncing immediately for better user feedback */
      this.markPageAsSyncing(pageName, this.instance);

      this.completeStep(pageName, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => { });
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Toggle form visibility, reset validation when reopening */
    toggleFormState(hide) {
      this.formHidden = hide;
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    /* SSN field focus methods */
    touchSSN() {
      // Mark SSN validation as dirty
      this.$v.ssn.$touch();
    },
    focusSSN2() {
      if (this.ssn1 && this.ssn1.length === 3) {
        this.$refs.ssn2.focus();
      }
    },
    focusSSN3() {
      if (this.ssn2 && this.ssn2.length === 2) {
        this.$refs.ssn3.focus();
      }
    },

    /* Handle sync status changes (hide form after successful sync) */
    handleSyncStatusChange() {
      if (this.isPershingInheritedIRAComplete(this.instance)) {
        this.formHidden = true;
      }
    },

    /*
      Check if Pershing Inherited IRA page is complete for a given account instance
      (mirrors other page-completion helpers in the app)
    */
    isPershingInheritedIRAComplete(instance) {
      if (!this.clientProgress || !this.clientProgress.progress) return false;

      const page = this.clientProgress.progress.find(
        (p) =>
          p.pageName === MENUS.CLIENT.PERSHING_DECEDENT.id &&
          (!instance || (p.instance === instance.name && p.label === instance.label))
      );

      return page ? page.lastCompleted !== null && page.status === 'SYNCED' : false;
    },
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();

    // Determine the current page index based on the pathname (works for instance-based routes)
    this.pageIndex = undefined;
    let i = 0;
    for (const page of this.pages) {
      if (
        typeof page === 'object' &&
        page.pathname &&
        window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))
      ) {
        this.pageIndex = i;
        break;
      } else if (page === MENUS.CLIENT.PERSHING_DECEDENT.id) {
        // Fallback – unlikely for instance pages, but covers direct string usage
        this.pageIndex = i;
        break;
      }
      i++;
    }

    // Safety fallback if index still undefined
    if (this.pageIndex === undefined) {
      this.pageIndex = this.pages.findIndex((p) => (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.PERSHING_DECEDENT.id);
    }
    this.formHidden = this.isPershingInheritedIRAComplete(this.instance);

    // Initialize relationship options
    this.relationshipOptions = [];
    Object.keys(DECEDENT_RELATIONSHIP_TYPES).map((key) => {
      const val = DECEDENT_RELATIONSHIP_TYPES[key];
      this.relationshipOptions.push(val);
    });

    // Initialize structure options
    this.structureOptions = [];
    Object.keys(DECEDENT_INHERITANCE_STRUCTURE_TYPES).map((key) => {
      const val = DECEDENT_INHERITANCE_STRUCTURE_TYPES[key];
      this.structureOptions.push(val);
    });

    // Initialize individual inheritance types
    this.individualTypes = [];
    Object.keys(DECEDENT_INDIVIDUAL_INHERITANCE_TYPES).map((key) => {
      const val = DECEDENT_INDIVIDUAL_INHERITANCE_TYPES[key];
      this.individualTypes.push(val);
    });

    // Initialize entity inheritance types
    this.entityTypes = [];
    Object.keys(DECEDENT_ENTITY_INHERITANCE_TYPES).map((key) => {
      const val = DECEDENT_ENTITY_INHERITANCE_TYPES[key];
      this.entityTypes.push(val);
    });

    // Initialize payout options
    this.payoutOptions = [];
    Object.keys(DECEDENT_PAYOUT_TYPES).map((key) => {
      const val = DECEDENT_PAYOUT_TYPES[key];
      this.payoutOptions.push(val);
    });

    // Initialize country options
    this.countryOptions = COUNTRIES;

    // Initialize wealth source options
    this.wealthSourceOptions = [];
    Object.keys(WEALTH_SOURCE_LIST).map((key) => {
      const val = WEALTH_SOURCE_LIST[key];
      this.wealthSourceOptions.push(val);
    });
  },
};
</script>

<style lang="scss" scoped>
.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}

.syncing-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.form-hidden {
  .step-form-container {
    display: none;
  }
}

.help-link {
  padding-top: 8px;
  
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.faux-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 8px;
  display: block;
  
  &.required::after {
    content: ' *';
    color: red;
  }
}

.ssn-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  
  .ssn1, .ssn2, .ssn3 {
    flex: 0 0 auto;
  }
  
  .ssn1 {
    width: 80px;
  }
  
  .ssn2 {
    width: 60px;
  }
  
  .ssn3 {
    width: 100px;
  }
  
  .dash {
    color: #666;
    font-size: 14px;
    margin: 0 4px;
  }
}

.error-area {
  color: #ff5252;
  font-size: 12px;
  margin-top: -8px;
  margin-bottom: 16px;
}
</style>
